import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { XMarkIcon, PaperAirplaneIcon, CheckIcon, TrashIcon, PencilIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';

function ChatPanel({ sessionId, isOpen, onClose, onHandsSaved }) {
    const [messages, setMessages] = useState([]);
    const [pendingHands, setPendingHands] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [editingHand, setEditingHand] = useState(null);
    const [savingAll, setSavingAll] = useState(false);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    // Fetch chat history and pending hands
    const fetchChatData = useCallback(async () => {
        if (!sessionId) return;
        setLoading(true);
        try {
            const [chatResponse, pendingResponse] = await Promise.all([
                axios.get(`/api/chat/session/${sessionId}/`),
                axios.get(`/api/chat/pending/${sessionId}/`)
            ]);
            setMessages(chatResponse.data.messages || []);
            setPendingHands(pendingResponse.data || []);
        } catch (err) {
            console.error('Failed to fetch chat data:', err);
        } finally {
            setLoading(false);
        }
    }, [sessionId]);

    useEffect(() => {
        if (isOpen && sessionId) {
            fetchChatData();
        }
    }, [isOpen, sessionId, fetchChatData]);

    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Focus input when panel opens
    useEffect(() => {
        if (isOpen) {
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [isOpen]);

    const sendMessage = async () => {
        if (!inputMessage.trim() || sending) return;

        const userMessage = inputMessage.trim();
        setInputMessage('');
        setSending(true);

        // Optimistically add user message
        const tempUserMsg = {
            id: `temp-${Date.now()}`,
            role: 'user',
            content: userMessage,
            created_at: new Date().toISOString()
        };
        setMessages(prev => [...prev, tempUserMsg]);

        try {
            const response = await axios.post('/api/chat/', {
                session_id: sessionId,
                message: userMessage
            });

            // Replace temp message with actual messages from server
            setMessages(prev => {
                const filtered = prev.filter(m => m.id !== tempUserMsg.id);
                return [...filtered,
                    { id: `user-${Date.now()}`, role: 'user', content: userMessage, created_at: new Date().toISOString() },
                    response.data.message
                ];
            });

            // Add extracted hand if present
            if (response.data.extracted_hand) {
                setPendingHands(prev => [...prev, response.data.extracted_hand]);
            }
        } catch (err) {
            console.error('Failed to send message:', err);
            // Show error in chat
            setMessages(prev => [...prev.filter(m => m.id !== tempUserMsg.id), {
                id: `error-${Date.now()}`,
                role: 'assistant',
                content: `Error: ${err.response?.data?.error || 'Failed to get response'}`,
                created_at: new Date().toISOString()
            }]);
        } finally {
            setSending(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const updatePendingHand = async (handId, updates) => {
        try {
            const response = await axios.patch(`/api/chat/pending/hand/${handId}/`, updates);
            setPendingHands(prev => prev.map(h => h.id === handId ? response.data : h));
            setEditingHand(null);
        } catch (err) {
            console.error('Failed to update hand:', err);
        }
    };

    const rejectPendingHand = async (handId) => {
        try {
            await axios.delete(`/api/chat/pending/hand/${handId}/`);
            setPendingHands(prev => prev.filter(h => h.id !== handId));
        } catch (err) {
            console.error('Failed to reject hand:', err);
        }
    };

    const saveAllHands = async () => {
        setSavingAll(true);
        try {
            const response = await axios.post(`/api/chat/save/${sessionId}/`);
            setPendingHands([]);
            if (onHandsSaved) {
                onHandsSaved(response.data.saved_count);
            }
        } catch (err) {
            console.error('Failed to save hands:', err);
        } finally {
            setSavingAll(false);
        }
    };

    const formatTime = (dateString) => {
        return new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const getHandTypeLabel = (type) => {
        const labels = {
            'big_hand': 'Notable Hand',
            'big_fold': 'Discipline Fold',
            'curiosity_call': 'Curiosity Call'
        };
        return labels[type] || type;
    };

    const getHandTypeColor = (type) => {
        const colors = {
            'big_hand': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
            'big_fold': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
            'curiosity_call': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
        };
        return colors[type] || 'bg-gray-100 text-gray-800';
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-end z-50">
            <div className="bg-white dark:bg-gray-800 w-full max-w-lg h-full flex flex-col shadow-xl">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-2">
                        <ChatBubbleLeftRightIcon className="h-6 w-6 text-green-600" />
                        <h2 className="text-lg font-bold text-gray-900 dark:text-white">Session Coach</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                    >
                        <XMarkIcon className="h-5 w-5 text-gray-500" />
                    </button>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {loading ? (
                        <div className="flex items-center justify-center h-full">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                        </div>
                    ) : messages.length === 0 ? (
                        <div className="text-center text-gray-500 dark:text-gray-400 mt-8">
                            <ChatBubbleLeftRightIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
                            <p className="text-sm">Describe hands you played and get coaching advice.</p>
                            <p className="text-xs mt-2 text-gray-400">Example: "Lost $500 with AA vs a set on a dry board"</p>
                        </div>
                    ) : (
                        messages.map((msg) => (
                            <div
                                key={msg.id}
                                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                            >
                                <div
                                    className={`max-w-[85%] rounded-lg p-3 ${
                                        msg.role === 'user'
                                            ? 'bg-green-600 text-white'
                                            : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                                    }`}
                                >
                                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                                    <p className={`text-xs mt-1 ${
                                        msg.role === 'user' ? 'text-green-200' : 'text-gray-400'
                                    }`}>
                                        {formatTime(msg.created_at)}
                                    </p>
                                </div>
                            </div>
                        ))
                    )}
                    <div ref={messagesEndRef} />
                </div>

                {/* Pending Hands Section */}
                {pendingHands.length > 0 && (
                    <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                Extracted Hands ({pendingHands.length})
                            </h3>
                            <button
                                onClick={saveAllHands}
                                disabled={savingAll}
                                className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50"
                            >
                                {savingAll ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                ) : (
                                    <CheckIcon className="h-4 w-4" />
                                )}
                                Save All
                            </button>
                        </div>
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                            {pendingHands.map((hand) => (
                                <div
                                    key={hand.id}
                                    className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700"
                                >
                                    {editingHand === hand.id ? (
                                        <EditHandForm
                                            hand={hand}
                                            onSave={(updates) => updatePendingHand(hand.id, updates)}
                                            onCancel={() => setEditingHand(null)}
                                        />
                                    ) : (
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className={`text-xs px-2 py-0.5 rounded-full ${getHandTypeColor(hand.hand_type)}`}>
                                                        {getHandTypeLabel(hand.hand_type)}
                                                    </span>
                                                    {hand.amount && (
                                                        <span className="text-xs text-gray-500">
                                                            ${parseFloat(hand.amount).toFixed(0)}
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-sm text-gray-700 dark:text-gray-300 truncate">
                                                    {hand.hand_description}
                                                </p>
                                                {hand.tags && hand.tags.length > 0 && (
                                                    <div className="flex flex-wrap gap-1 mt-1">
                                                        {hand.tags.map(tag => (
                                                            <span key={tag} className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-1.5 py-0.5 rounded">
                                                                {tag}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <button
                                                    onClick={() => setEditingHand(hand.id)}
                                                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                                                >
                                                    <PencilIcon className="h-4 w-4 text-gray-400" />
                                                </button>
                                                <button
                                                    onClick={() => rejectPendingHand(hand.id)}
                                                    className="p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"
                                                >
                                                    <TrashIcon className="h-4 w-4 text-red-400" />
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Input */}
                <div className="border-t border-gray-200 dark:border-gray-700 p-4">
                    <div className="flex items-end gap-2">
                        <textarea
                            ref={inputRef}
                            value={inputMessage}
                            onChange={(e) => setInputMessage(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Describe a hand or ask for advice..."
                            className="flex-1 resize-none rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            rows={2}
                            disabled={sending}
                        />
                        <button
                            onClick={sendMessage}
                            disabled={!inputMessage.trim() || sending}
                            className="p-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {sending ? (
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                            ) : (
                                <PaperAirplaneIcon className="h-5 w-5" />
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Edit form for pending hands
function EditHandForm({ hand, onSave, onCancel }) {
    const [formData, setFormData] = useState({
        hand_type: hand.hand_type,
        hand_description: hand.hand_description,
        amount: hand.amount || '',
        outcome: hand.outcome || 'lost',
        difficulty: hand.difficulty || 'easy',
        severity: hand.severity || 'pretty_bad',
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
                <select
                    value={formData.hand_type}
                    onChange={(e) => setFormData({ ...formData, hand_type: e.target.value })}
                    className="text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-gray-900 dark:text-white"
                >
                    <option value="big_hand">Notable Hand</option>
                    <option value="big_fold">Discipline Fold</option>
                    <option value="curiosity_call">Curiosity Call</option>
                </select>
                <input
                    type="number"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="Amount"
                    className="text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-gray-900 dark:text-white"
                />
            </div>
            <textarea
                value={formData.hand_description}
                onChange={(e) => setFormData({ ...formData, hand_description: e.target.value })}
                className="w-full text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-gray-900 dark:text-white"
                rows={2}
            />
            {formData.hand_type === 'big_hand' && (
                <select
                    value={formData.outcome}
                    onChange={(e) => setFormData({ ...formData, outcome: e.target.value })}
                    className="w-full text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-gray-900 dark:text-white"
                >
                    <option value="won">Won</option>
                    <option value="lost">Lost</option>
                    <option value="chopped">Chopped</option>
                </select>
            )}
            {formData.hand_type === 'big_fold' && (
                <select
                    value={formData.difficulty}
                    onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })}
                    className="w-full text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-gray-900 dark:text-white"
                >
                    <option value="easy">Easy Fold</option>
                    <option value="hard">Hard Fold</option>
                </select>
            )}
            {formData.hand_type === 'curiosity_call' && (
                <select
                    value={formData.severity}
                    onChange={(e) => setFormData({ ...formData, severity: e.target.value })}
                    className="w-full text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-gray-900 dark:text-white"
                >
                    <option value="pretty_bad">Pretty Bad</option>
                    <option value="catastrophic">Catastrophic</option>
                </select>
            )}
            <div className="flex gap-2">
                <button
                    type="button"
                    onClick={onCancel}
                    className="flex-1 text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    className="flex-1 text-xs px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                >
                    Save
                </button>
            </div>
        </form>
    );
}

export default ChatPanel;
